import React, { useState } from 'react';
import { 
  Upload, 
  FileText, 
  CheckCircle, 
  ArrowRight,
  Loader2,
  Edit3,
  Save,
  X
} from 'lucide-react';
import { generateV3Scripts } from '../services/api';

const V3Workflow = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [workflowData, setWorkflowData] = useState({
    articleId: null,
    articleTitle: '',
    articleContent: '',
    summary: '',
    analysis: null,
    selectedConcepts: [],
    generatedScripts: [],
    step1Completed: false,
    step2Completed: false,
    step3Completed: false,
    step4Completed: false,
  });

  const steps = [
    { id: 1, title: 'Upload PDF', icon: Upload, isCompleted: workflowData.step1Completed },
    { id: 2, title: 'Analyze Article', icon: FileText, isCompleted: workflowData.step2Completed },
    { id: 3, title: 'Select Concepts', icon: CheckCircle, isCompleted: workflowData.step3Completed },
    { id: 4, title: 'Generate Scripts', icon: FileText, isCompleted: workflowData.step4Completed },
    { id: 5, title: 'Create Audio', icon: Upload, isCompleted: false },
    { id: 6, title: 'Generate Videos', icon: Upload, isCompleted: false },
  ];

  const isStepEnabled = (stepId) => {
    switch (stepId) {
      case 1: return true;
      case 2: return workflowData.step1Completed;
      case 3: return workflowData.step2Completed;
      case 4: return workflowData.step3Completed;
      case 5: return workflowData.step4Completed;
      case 6: return false; // Will be updated later
      default: return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return <Step1UploadPDF workflowData={workflowData} setWorkflowData={setWorkflowData} onNext={() => setCurrentStep(2)} />;
      case 2: return <Step2ReviewSummary workflowData={workflowData} setWorkflowData={setWorkflowData} onNext={() => setCurrentStep(3)} />;
      case 3: return <Step3SelectConcepts workflowData={workflowData} setWorkflowData={setWorkflowData} onNext={() => setCurrentStep(4)} />;
      case 4: return <Step4GenerateScripts workflowData={workflowData} setWorkflowData={setWorkflowData} onNext={() => setCurrentStep(5)} />;
      case 5: return <div className="p-8 text-center text-gray-500">Step 5 - Coming Soon</div>;
      case 6: return <div className="p-8 text-center text-gray-500">Step 6 - Coming Soon</div>;
      default: return <div className="p-8 text-center text-gray-500">Unknown step</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Left Sidebar */}
        <div className="w-80 bg-white shadow-lg border-r border-gray-200">
          <div className="p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-8">Video Creation Workflow</h1>
            
            <div className="space-y-2">
              {steps.map((step) => {
                const Icon = step.icon;
                const isEnabled = isStepEnabled(step.id);
                const isActive = currentStep === step.id;
                const isCompleted = step.isCompleted;
                
                return (
                  <button
                    key={step.id}
                    onClick={() => isEnabled && setCurrentStep(step.id)}
                    disabled={!isEnabled}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                      isActive
                        ? 'bg-purple-100 text-purple-700 border-2 border-purple-300'
                        : isEnabled
                        ? 'hover:bg-gray-100 text-gray-700'
                        : 'text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      isCompleted
                        ? 'bg-green-500 text-white'
                        : isActive
                        ? 'bg-purple-500 text-white'
                        : isEnabled
                        ? 'bg-gray-300 text-gray-600'
                        : 'bg-gray-200 text-gray-400'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <Icon className="w-5 h-5" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{step.title}</div>
                      <div className="text-sm opacity-75">
                        {step.id === 1 && 'Upload and prepare your PDF'}
                        {step.id === 2 && 'Review AI-generated summary'}
                        {step.id === 3 && 'Select script outlines'}
                        {step.id === 4 && 'Generate video scripts'}
                        {step.id === 5 && 'Create audio tracks'}
                        {step.id === 6 && 'Generate final videos'}
                      </div>
                    </div>
                    {isActive && (
                      <ArrowRight className="w-4 h-4 text-purple-600" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="p-8">
            {renderStepContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

// Step 1: Upload PDF
const Step1UploadPDF = ({ workflowData, setWorkflowData, onNext }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(workflowData.articleTitle || '');

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    console.log('Drop event triggered');
    console.log('Files:', e.dataTransfer.files);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      console.log('File dropped:', e.dataTransfer.files[0]);
      handleFile(e.dataTransfer.files[0]);
    } else {
      console.log('No files in drop event');
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    console.log('handleFile called with:', file);
    console.log('File type:', file.type);
    console.log('File name:', file.name);
    
    if (file.type !== 'application/pdf') {
      console.log('Invalid file type:', file.type);
      setUploadStatus({ type: 'error', message: 'Please upload a PDF file.' });
      return;
    }

    console.log('Starting upload process...');
    setUploading(true);
    setUploadStatus(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      console.log('Upload response:', result);
      
      setWorkflowData(prev => ({
        ...prev,
        articleId: result.id,
        articleTitle: result.title || file.name.replace('.pdf', ''),
        articleContent: result.content || '',
        step1Completed: true
      }));
      
      setTempTitle(result.title || file.name.replace('.pdf', ''));
      setUploadStatus({ type: 'success', message: 'PDF uploaded successfully!' });
      console.log('Workflow data updated:', workflowData);
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus({ 
        type: 'error', 
        message: 'Failed to upload PDF. Please try again.' 
      });
    } finally {
      setUploading(false);
    }
  };

  const handleTitleSave = () => {
    setWorkflowData(prev => ({
      ...prev,
      articleTitle: tempTitle
    }));
    setEditingTitle(false);
  };

  const handleCreateSummary = async () => {
    if (!workflowData.articleId) {
      setUploadStatus({ type: 'error', message: 'Please upload a PDF first.' });
      return;
    }

    setUploading(true);
    setUploadStatus(null);

    try {
      const response = await fetch(`/api/v2/generate-summary?article_id=${workflowData.articleId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Summary generation failed');
      }

      const result = await response.json();
      
      setWorkflowData(prev => ({
        ...prev,
        summary: result.summary,
        step2Completed: true
      }));
      
      setUploadStatus({ type: 'success', message: 'Summary generated successfully!' });
      onNext(); // Move to step 2
      
    } catch (error) {
      console.error('Summary generation error:', error);
      setUploadStatus({ 
        type: 'error', 
        message: 'Failed to generate summary. Please try again.' 
      });
    } finally {
      setUploading(false);
    }
  };

  // Debug logging
  console.log('Step1UploadPDF render - workflowData:', workflowData);
  console.log('Step1UploadPDF render - uploading:', uploading);
  console.log('Step1UploadPDF render - uploadStatus:', uploadStatus);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <Upload className="w-16 h-16 text-purple-600 mx-auto mb-4" />
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Upload Your PDF</h2>
        <p className="text-gray-600">
          Upload a YPN magazine article to create engaging video content
        </p>
      </div>

      {/* Upload Area */}
      {!workflowData.articleId && (
        <div
          className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
            dragActive
              ? 'border-purple-500 bg-purple-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg text-gray-600 mb-4">
            Drag and drop your PDF here, or{' '}
            <label className="text-purple-600 hover:text-purple-700 cursor-pointer font-medium">
              browse to upload
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileInput}
                className="hidden"
              />
            </label>
          </p>
          <p className="text-sm text-gray-500">
            Supported format: PDF files only
          </p>
        </div>
      )}

      {/* Upload Status */}
      {uploadStatus && (
        <div className={`mt-6 p-4 rounded-lg ${
          uploadStatus.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          <div className="flex items-center">
            {uploadStatus.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2" />
            ) : (
              <X className="w-5 h-5 mr-2" />
            )}
            {uploadStatus.message}
          </div>
        </div>
      )}

      {/* Title Editing */}
      {workflowData.articleId && (
        <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Article Title</h3>
          
          {editingTitle ? (
            <div className="space-y-4">
              <input
                type="text"
                value={tempTitle}
                onChange={(e) => setTempTitle(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Enter article title..."
              />
              <div className="flex space-x-3">
                <button
                  onClick={handleTitleSave}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <Save className="w-4 h-4" />
                  <span>Save</span>
                </button>
                <button
                  onClick={() => {
                    setTempTitle(workflowData.articleTitle);
                    setEditingTitle(false);
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-lg text-gray-900">{workflowData.articleTitle}</p>
              <button
                onClick={() => setEditingTitle(true)}
                className="flex items-center space-x-2 px-3 py-2 text-purple-600 hover:text-purple-700"
              >
                <Edit3 className="w-4 h-4" />
                <span>Edit</span>
              </button>
            </div>
          )}

          {/* Create Summary Button */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={handleCreateSummary}
              disabled={uploading}
              className="w-full flex items-center justify-center space-x-3 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Creating Summary...</span>
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  <span>Create Summary</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Step 2: Review Summary
const Step2ReviewSummary = ({ workflowData, setWorkflowData, onNext }) => {
  const [editingSummary, setEditingSummary] = useState(false);
  const [tempSummary, setTempSummary] = useState(workflowData.summary || '');
  const [processing, setProcessing] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSummarySave = () => {
    setWorkflowData(prev => ({
      ...prev,
      summary: tempSummary
    }));
    setEditingSummary(false);
  };

  const handleAccept = async () => {
    if (!workflowData.articleId) {
      setStatus({ type: 'error', message: 'No article ID found.' });
      return;
    }

    setProcessing(true);
    setStatus(null);

    try {
      // Perform comprehensive analysis using the new V3 approach
      const formData = new FormData();
      if (workflowData.summary) {
        formData.append('user_notes', `Summary: ${workflowData.summary}`);
      }

      const analysisResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v3/analyze-article?article_id=${workflowData.articleId}`, {
        method: 'POST',
        body: formData,
      });

      if (!analysisResponse.ok) {
        throw new Error('Failed to analyze article');
      }

      const analysisData = await analysisResponse.json();
      
      // Store the comprehensive analysis
      setWorkflowData(prev => ({
        ...prev,
        analysis: analysisData.analysis,
        step2Completed: true
      }));

      setStatus({ type: 'success', message: 'Article analysis completed successfully!' });
      onNext(); // Move to step 3
      
    } catch (error) {
      console.error('Accept error:', error);
      setStatus({ 
        type: 'error', 
        message: `Failed to analyze article: ${error.message}` 
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <FileText className="w-16 h-16 text-purple-600 mx-auto mb-4" />
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Analyze Article</h2>
        <p className="text-gray-600">
          Review and edit the summary, then run comprehensive analysis to identify video concepts
        </p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">Article Summary</h3>
          {!editingSummary && (
            <button
              onClick={() => setEditingSummary(true)}
              className="flex items-center space-x-2 px-3 py-2 text-purple-600 hover:text-purple-700"
            >
              <Edit3 className="w-4 h-4" />
              <span>Edit</span>
            </button>
          )}
        </div>

        {editingSummary ? (
          <div className="space-y-4">
            <textarea
              value={tempSummary}
              onChange={(e) => setTempSummary(e.target.value)}
              rows={12}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Enter article summary..."
            />
            <div className="flex space-x-3">
              <button
                onClick={handleSummarySave}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <Save className="w-4 h-4" />
                <span>Save</span>
              </button>
              <button
                onClick={() => {
                  setTempSummary(workflowData.summary);
                  setEditingSummary(false);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="prose max-w-none">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {workflowData.summary}
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-center space-x-4 pt-6 border-t border-gray-200">
              <button
                onClick={handleAccept}
                disabled={processing}
                className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                        <CheckCircle className="w-5 h-5" />
                        <span>Accept & Analyze Article</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Status Messages */}
      {status && (
        <div className={`mt-6 p-4 rounded-lg ${
          status.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          <div className="flex items-center">
            {status.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2" />
            ) : (
              <X className="w-5 h-5 mr-2" />
            )}
            {status.message}
          </div>
        </div>
      )}
    </div>
  );
};

// Step 3: Select Video Concepts
const Step3SelectConcepts = ({ workflowData, setWorkflowData, onNext }) => {
  const [selectedConcepts, setSelectedConcepts] = useState([]);

  const handleConceptSelect = (conceptId) => {
    const isSelected = selectedConcepts.includes(conceptId);
    if (isSelected) {
      setSelectedConcepts(selectedConcepts.filter(id => id !== conceptId));
    } else {
      if (selectedConcepts.length < 3) {
        setSelectedConcepts([...selectedConcepts, conceptId]);
      }
    }
  };

  const handleProceed = () => {
    if (selectedConcepts.length === 0) {
      return;
    }

    // Update workflow data with selected concepts
    setWorkflowData(prev => ({
      ...prev,
      selectedConcepts: selectedConcepts,
      step3Completed: true
    }));

    onNext();
  };

  if (!workflowData.analysis) {
    return (
      <div className="max-w-4xl mx-auto text-center py-8">
        <p className="text-gray-600">No analysis data available. Please go back to Step 2 and run the analysis.</p>
      </div>
    );
  }

  const { opportunities, case_studies, strategies, personal_journey } = workflowData.analysis;
  const allConcepts = [
    ...(opportunities?.shorts || []).map(concept => ({ ...concept, type: 'short' })),
    ...(opportunities?.long_form || []).map(concept => ({ ...concept, type: 'long_form' }))
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-8">
        <CheckCircle className="w-16 h-16 text-purple-600 mx-auto mb-4" />
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Select Video Concepts</h2>
        <p className="text-gray-600">
          Choose up to 3 video concepts to proceed with script generation
        </p>
        {selectedConcepts.length > 0 && (
          <p className="text-sm text-purple-600 font-medium mt-2">
            {selectedConcepts.length} concept{selectedConcepts.length > 1 ? 's' : ''} selected
          </p>
        )}
      </div>

      {/* Analysis Summary */}
      <div className="mb-8 grid gap-4 md:grid-cols-3">
        {case_studies?.count > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">Case Studies</h3>
            <p className="text-blue-700">{case_studies.count} found</p>
          </div>
        )}
        {strategies?.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-900 mb-2">Strategies</h3>
            <p className="text-green-700">{strategies.length} identified</p>
          </div>
        )}
        {personal_journey?.themes?.length > 0 && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="font-semibold text-purple-900 mb-2">Personal Journey</h3>
            <p className="text-purple-700">{personal_journey.themes.length} themes</p>
          </div>
        )}
      </div>

      {/* Video Concepts */}
      {allConcepts.length > 0 ? (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-gray-900">Available Video Concepts</h3>
          <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
            {allConcepts.map((concept) => (
              <div
                key={concept.id}
                className={`relative bg-white rounded-lg border-2 p-6 cursor-pointer transition-all duration-200 hover:shadow-lg ${
                  selectedConcepts.includes(concept.id)
                    ? 'border-purple-500 bg-purple-50 shadow-lg'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleConceptSelect(concept.id)}
              >
                <div className={`absolute top-4 right-4 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedConcepts.includes(concept.id)
                    ? 'border-purple-500 bg-purple-500'
                    : 'border-gray-300'
                }`}>
                  {selectedConcepts.includes(concept.id) && (
                    <CheckCircle className="w-4 h-4 text-white" />
                  )}
                </div>

                <div className="pr-8">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      concept.type === 'short' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {concept.type === 'short' ? 'Short Form' : 'Long Form'}
                    </span>
                    <span className="text-sm text-gray-500">
                      {concept.target_length_sec}s
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {concept.angle}
                  </h3>
                  
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-medium text-gray-700 mb-1">Hook:</h4>
                      <p className="text-sm text-gray-600">{concept.hook}</p>
                    </div>
                    
                    {concept.beats && (
                      <div>
                        <h4 className="font-medium text-gray-700 mb-1">Key Points:</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {concept.beats.map((beat, index) => (
                            <li key={index}>• {beat}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <div>
                      <h4 className="font-medium text-gray-700 mb-1">CTA:</h4>
                      <p className="text-sm text-gray-600">{concept.cta}</p>
                    </div>
                    
                    {concept.why_it_works && (
                      <div>
                        <h4 className="font-medium text-gray-700 mb-1">Why it works:</h4>
                        <p className="text-sm text-gray-600">{concept.why_it_works}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-600">No video concepts available. Please check the analysis results.</p>
        </div>
      )}

      {/* Proceed Button */}
      {selectedConcepts.length > 0 && (
        <div className="mt-8 flex justify-center">
          <button
            onClick={handleProceed}
            className="flex items-center space-x-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <ArrowRight className="w-5 h-5" />
            <span>Proceed with {selectedConcepts.length} Concept{selectedConcepts.length > 1 ? 's' : ''}</span>
          </button>
        </div>
      )}
    </div>
  );
};

// Step 4: Generate Scripts
const Step4GenerateScripts = ({ workflowData, setWorkflowData, onNext }) => {
  const [generating, setGenerating] = useState(false);
  const [status, setStatus] = useState(null);
  const [editingScripts, setEditingScripts] = useState({});

  const handleGenerateScripts = async () => {
    if (!workflowData.articleId || !workflowData.selectedConcepts.length) {
      setStatus({ type: 'error', message: 'No article ID or selected concepts found.' });
      return;
    }

    setGenerating(true);
    setStatus(null);

    try {
      const response = await generateV3Scripts(workflowData.articleId, workflowData.selectedConcepts);
      
      if (response.success) {
        setWorkflowData(prev => ({
          ...prev,
          generatedScripts: response.scripts,
          step4Completed: true
        }));
        
        setStatus({ type: 'success', message: response.message });
      } else {
        throw new Error(response.message || 'Failed to generate scripts');
      }
      
    } catch (error) {
      console.error('Script generation error:', error);
      setStatus({ 
        type: 'error', 
        message: `Failed to generate scripts: ${error.message}` 
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleScriptEdit = (scriptIndex, newScript) => {
    setEditingScripts(prev => ({
      ...prev,
      [scriptIndex]: newScript
    }));
  };

  const handleScriptSave = (scriptIndex) => {
    const editedScript = editingScripts[scriptIndex];
    if (editedScript) {
      setWorkflowData(prev => ({
        ...prev,
        generatedScripts: prev.generatedScripts.map((script, index) => 
          index === scriptIndex ? { ...script, script: editedScript } : script
        )
      }));
      
      setEditingScripts(prev => {
        const newEditing = { ...prev };
        delete newEditing[scriptIndex];
        return newEditing;
      });
    }
  };

  const handleProceed = () => {
    if (workflowData.generatedScripts.length === 0) {
      return;
    }
    onNext();
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-8">
        <FileText className="w-16 h-16 text-purple-600 mx-auto mb-4" />
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Generate Scripts</h2>
        <p className="text-gray-600">
          Generate video scripts from your selected concepts
        </p>
      </div>

      {/* Selected Concepts Summary */}
      <div className="mb-8 bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Selected Concepts</h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {workflowData.selectedConcepts.map((conceptId) => {
            // Find the concept details from the analysis
            const allConcepts = [
              ...(workflowData.analysis?.opportunities?.shorts || []),
              ...(workflowData.analysis?.opportunities?.long_form || [])
            ];
            const concept = allConcepts.find(c => c.id === conceptId);
            
            if (!concept) return null;
            
            return (
              <div key={conceptId} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    concept.type === 'short' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {concept.type === 'short' ? 'Short Form' : 'Long Form'}
                  </span>
                  <span className="text-sm text-gray-500">
                    {concept.target_length_sec}s
                  </span>
                </div>
                <h4 className="font-medium text-gray-900">{concept.angle}</h4>
              </div>
            );
          })}
        </div>
      </div>

      {/* Generate Scripts Button */}
      {workflowData.generatedScripts.length === 0 && (
        <div className="text-center mb-8">
          <button
            onClick={handleGenerateScripts}
            disabled={generating}
            className="flex items-center space-x-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed mx-auto"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Generating Scripts...</span>
              </>
            ) : (
              <>
                <FileText className="w-5 h-5" />
                <span>Generate Scripts</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Generated Scripts */}
      {workflowData.generatedScripts.length > 0 && (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-gray-900">Generated Scripts</h3>
          {workflowData.generatedScripts.map((scriptData, index) => (
            <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    scriptData.concept_type === 'short' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {scriptData.concept_type === 'short' ? 'Short Form' : 'Long Form'}
                  </span>
                  <span className="text-sm text-gray-500">
                    {scriptData.target_length_sec}s
                  </span>
                </div>
                <button
                  onClick={() => {
                    if (editingScripts[index]) {
                      handleScriptSave(index);
                    } else {
                      setEditingScripts(prev => ({
                        ...prev,
                        [index]: scriptData.script
                      }));
                    }
                  }}
                  className="flex items-center space-x-2 px-3 py-2 text-purple-600 hover:text-purple-700"
                >
                  {editingScripts[index] ? (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Save</span>
                    </>
                  ) : (
                    <>
                      <Edit3 className="w-4 h-4" />
                      <span>Edit</span>
                    </>
                  )}
                </button>
              </div>
              
              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                {scriptData.concept_angle}
              </h4>
              
              {editingScripts[index] !== undefined ? (
                <textarea
                  value={editingScripts[index]}
                  onChange={(e) => handleScriptEdit(index, e.target.value)}
                  rows={12}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              ) : (
                <div className="prose max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {scriptData.script}
                  </p>
                </div>
              )}
            </div>
          ))}
          
          {/* Proceed Button */}
          <div className="text-center">
            <button
              onClick={handleProceed}
              className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <ArrowRight className="w-5 h-5" />
              <span>Proceed to Audio Generation</span>
            </button>
          </div>
        </div>
      )}

      {/* Status Messages */}
      {status && (
        <div className={`mt-6 p-4 rounded-lg ${
          status.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          <div className="flex items-center">
            {status.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2" />
            ) : (
              <X className="w-5 h-5 mr-2" />
            )}
            {status.message}
          </div>
        </div>
      )}
    </div>
  );
};

export default V3Workflow;
